import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for handling consensus gene filtering
 * @param {Array} searchResults - The original search results
 * @param {Function} filterFn - Function that determines if an item matches the consensus filter
 * @returns {Object} - State and functions for consensus filtering
 */
export const useConsensusFilter = (searchResults, filterFn) => {
  const [consensusFilterEnabled, setConsensusFilterEnabled] = useState(false);
  const [consensusGenes, setConsensusGenes] = useState([]);
  const [isLoadingConsensus, setIsLoadingConsensus] = useState(false);
  const [filteredResults, setFilteredResults] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  
  // Convert string or number to string for comparison
  const normalizeValue = useCallback((value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }, []);
  
  // Memoized includes function that handles type differences
  const includesNormalized = useCallback((arr, value) => {
    const normalizedValue = normalizeValue(value);
    return arr.some(item => normalizeValue(item) === normalizedValue);
  }, [normalizeValue]);
  
  // Fetch consensus genes on hook initialization
  useEffect(() => {
    fetchConsensusGenes();
  }, []);
  
  // Apply filter when consensus filter status changes or search results update
  useEffect(() => {
    if (consensusFilterEnabled && consensusGenes.length > 0) {
      const filtered = searchResults.filter(item => 
        filterFn(item, consensusGenes, includesNormalized)
      );
      setFilteredResults(filtered);
      console.log(`Filtered ${searchResults.length} results down to ${filtered.length} results`);
    } else {
      setFilteredResults(searchResults);
    }
  }, [consensusFilterEnabled, searchResults, consensusGenes, filterFn, includesNormalized]);
  
  // Function to fetch consensus genes from API
  const fetchConsensusGenes = async () => {
    setIsLoadingConsensus(true);
    setErrorMessage(null);
    try {
      const response = await fetch('/api/consensus/genes');
      const data = await response.json();
      if (data.success) {
        setConsensusGenes(data.data);
        console.log(`Loaded ${data.data.length} consensus genes`);
        // Log a few examples for debugging
        if (data.data.length > 0) {
          console.log(`Sample consensus genes: ${data.data.slice(0, 5)}`);
        }
      } else {
        console.error('Failed to fetch consensus genes:', data.message);
        setErrorMessage(`Failed to load consensus genes: ${data.message}`);
      }
    } catch (error) {
      console.error('Error fetching consensus genes:', error);
      setErrorMessage(`Error loading consensus genes: ${error.message}`);
    } finally {
      setIsLoadingConsensus(false);
    }
  };
  
  // Toggle consensus filter on/off
  const toggleConsensusFilter = () => {
    setConsensusFilterEnabled(prev => !prev);
  };
  
  return {
    consensusFilterEnabled,
    consensusGenes,
    isLoadingConsensus,
    filteredResults,
    toggleConsensusFilter,
    errorMessage,
    includesNormalized // Export the helper function
  };
};